import React, { useState } from "react";

type ValueType = "percentage" | "number";

interface DecisionCardProps {
  title: string;
  unit: string;
  value: string | number;
  value_type: ValueType;
  value_change: string | number;
  date: string; // MM/YY format
  secondary_value: string | number;
  secondary_value_type: ValueType;
  selected?: boolean;
  onClick?: () => void;
}

const formatValue = (value: string | number, type: ValueType, unit: string, showSymbol: boolean = true): string => {
  if (type === "percentage") {
    return `${value}%`;
  }

  if (unit === "yen" && showSymbol) {
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    return `¥${numValue.toLocaleString("en-US")}`;
  }

  if (typeof value === "number") {
    return value.toLocaleString("en-US");
  }

  return String(value);
};

const DecisionCard: React.FC<DecisionCardProps> = ({
  title,
  unit,
  value,
  value_type,
  value_change,
  date,
  secondary_value,
  secondary_value_type,
  selected = false,
  onClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const isPositive = Number(value_change) >= 0;
  const changeAbsolute = Math.abs(Number(value_change));

  const changeDisplay =
    secondary_value_type === "percentage"
      ? `${changeAbsolute}%`
      : `${Number(changeAbsolute).toLocaleString("en-US")}`;

  const titleWithSymbol = unit === "yen" ? title.replace(/\(yen symbol\)/i, "(¥)") : title;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: "flex",
        width: "278px",
        height: "116px",
        padding: "12px 16px",
        justifyContent: "space-between",
        alignItems: "flex-start",
        borderRadius: selected ? "12px" : "8px",
        border: selected ? "3px solid #64748B" : isHovered ? "1px solid #64748B" : "2px solid #CBD5E1",
        background: "#FFF",
        boxShadow: isHovered && !selected ? "0 4px 12px 0 rgba(82, 21, 163, 0.4)" : "none",
        cursor: "pointer",
        transition: "all 0.2s ease",
      }}
    >
      {/* Left Content */}
      <div style={{ display: "flex", flexDirection: "column", gap: "2px", flex: 1, width: "100%" }}>
        {/* Row 1: Title and Icon */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <span
            style={{
              color: "#64748B",
              fontFamily: "sans-serif",
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "16px",
            }}
          >
            {titleWithSymbol}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            style={{ flexShrink: 0 }}
          >
            <path
              d="M13.3333 4.66699H7.33332M9.33332 11.3337H3.33332M9.33332 11.3337C9.33332 12.4382 10.2288 13.3337 11.3333 13.3337C12.4379 13.3337 13.3333 12.4382 13.3333 11.3337C13.3333 10.2291 12.4379 9.33366 11.3333 9.33366C10.2288 9.33366 9.33332 10.2291 9.33332 11.3337ZM6.66666 4.66699C6.66666 5.77156 5.77123 6.66699 4.66666 6.66699C3.56209 6.66699 2.66666 5.77156 2.66666 4.66699C2.66666 3.56242 3.56209 2.66699 4.66666 2.66699C5.77123 2.66699 6.66666 3.56242 6.66666 4.66699Z"
              stroke="#0F172A"
              strokeWidth="1.33"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Row 2: Main Value */}
        <div
          style={{
            color: "#0F172A",
            fontFamily: "sans-serif",
            fontSize: "24px",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "32px",
          }}
        >
          {unit === "yen" ? (typeof value === "number" ? value.toLocaleString("en-US") : Number(value).toLocaleString("en-US")) : formatValue(value, value_type, unit, unit !== "yen")}
        </div>

        {/* Row 3: Change with Arrow */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {isPositive ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 5V19M12 5L6 11M12 5L18 11"
                stroke="#16A34A"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 19V5M12 19L6 13M12 19L18 13"
                stroke="#DC2626"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          <span
            style={{
              color: isPositive ? "#16A34A" : "#DC2626",
              fontFamily: "sans-serif",
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "16px",
            }}
          >
            {changeDisplay}
          </span>
        </div>

        {/* Row 4: Date and Secondary Value */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "#64748B",
            fontFamily: "sans-serif",
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: "16px",
          }}
        >
          <span>{date}</span>
          <span>: {unit === "yen" && secondary_value_type === "number" ? Number(secondary_value).toLocaleString("en-US") : formatValue(secondary_value, secondary_value_type, unit, unit !== "yen")}</span>
        </div>
      </div>
    </div>
  );
};

export default DecisionCard;
